import {
    type FC,
    type ReactNode,
    type ComponentProps,
    startTransition,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import {
    graphql,
    useFragment,
    usePreloadedQuery,
    type PreloadedQuery,
    useRefetchableFragment,
} from 'react-relay';
import classnames from 'classnames';

import { useVisibilityTracker } from 'dibs-visibility-tracker/exports/useVisibilityTracker.ts';
import { type VisibilityChangeCallback } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';

import {
    recentlyViewedItemsQuery,
    useRecentlyViewedItemsContext,
    MOBILE,
} from './RecentlyViewedItemsContext.tsx';
import {
    RecentlyViewedItemsWrapper,
    type RecentlyViewedItemsWrapperProps,
} from './RecentlyViewedItemsWrapper.tsx';
import { useRecentHistoryList } from './useRecentHistoryList.ts';
import { RecentlyViewedItemsHeart } from './RecentlyViewedItemsHeart.tsx';
import { ACTION_LABEL } from './constants.ts';
import { trackItemImpression, trackItemClick, trackArrowClick } from '../recentHistoryTracking.ts';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import { ScrollCarousel } from 'dibs-scroll-carousel/exports/ScrollCarousel.ts';
import {
    BuyerProductImageOnly,
    BuyerProductImageWithPrice,
    TILE_SIZES,
    getTileImageHeightPx,
    type TileSize,
} from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';

import { type RecentlyViewedItemsCarousel_viewer$key } from './__generated__/RecentlyViewedItemsCarousel_viewer.graphql.ts';
import { type RecentlyViewedItemsCarouselViewerRefetchQuery } from './__generated__/RecentlyViewedItemsCarouselViewerRefetchQuery.graphql.ts';
import { type RecentlyViewedItemsCarousel_user$key } from './__generated__/RecentlyViewedItemsCarousel_user.graphql.ts';
import { type RecentlyViewedItemsContextQuery } from './__generated__/RecentlyViewedItemsContextQuery.graphql.ts';
import { type ItemType } from '../recentHistoryTypes.ts';

import dibsCss from 'dibs-css';

import { type RecentlyViewedItemsCarousel_items$key } from './__generated__/RecentlyViewedItemsCarousel_items.graphql.ts';

const viewerFragment = graphql`
    fragment RecentlyViewedItemsCarousel_viewer on Viewer
    @refetchable(queryName: "RecentlyViewedItemsCarouselViewerRefetchQuery")
    @argumentDefinitions(
        loadPortfolioData: { type: "Boolean", defaultValue: false }
        selectedItemIds: { type: "[String]", defaultValue: [] }
        userIds: { type: "[String]", defaultValue: [] }
    ) {
        ...RecentlyViewedItemsHeart_viewer
            @arguments(
                loadPortfolioData: $loadPortfolioData
                selectedItemIds: $selectedItemIds
                userIds: $userIds
            )
    }
`;

const userFragment = graphql`
    fragment RecentlyViewedItemsCarousel_user on User
    @argumentDefinitions(
        excludeItemPks: { type: "[String]", defaultValue: [] }
        count: { type: "Int", defaultValue: 0 }
    ) {
        ...useRecentHistoryList_user @arguments(excludeItemPks: $excludeItemPks, count: $count)
        ...RecentlyViewedItemsHeart_user @arguments(excludeItemPks: $excludeItemPks, count: $count)
    }
`;

const itemsFragment = graphql`
    fragment RecentlyViewedItemsCarousel_items on Item @relay(plural: true) {
        ...useRecentHistoryList_items
        ...RecentlyViewedItemsHeart_items
    }
`;

type OnInitialDisplay = ({ items }: { items: ItemType[] }) => void;

type ScrollCarouselLayoutProps = Partial<
    Pick<
        ComponentProps<typeof ScrollCarousel>,
        | 'itemsToShow'
        | 'stepSize'
        | 'isInfinite'
        | 'showArrows'
        | 'arrowVariant'
        | 'gapSize'
        | 'scrollPadding'
        | 'shouldAdjustFlexBasis'
        | 'itemFlexBasis'
        | 'edgeFade'
    >
>;

export type RecentlyViewedItemsCarouselProps = {
    authModalLoader: $TSFixMe;
    queryReference: PreloadedQuery<RecentlyViewedItemsContextQuery>;
    trackItemClick?: typeof trackItemClick;
    trackItemImpression?: typeof trackItemImpression;
    onInitialDisplay?: OnInitialDisplay;
    isBot?: boolean;
    onEmpty?: () => void;
    trailingItem?: ReactNode;
    currency?: string;
    tileSize?: TileSize;
} & Omit<RecentlyViewedItemsWrapperProps, 'children'> &
    ScrollCarouselLayoutProps;

export const RecentlyViewedItemsCarousel: FC<RecentlyViewedItemsCarouselProps> = ({
    wrapperClassName,
    authModalLoader,
    queryReference,
    actionLabel = ACTION_LABEL,
    trackViewMoreClick,
    onInitialDisplay,
    hasTopSeparator = false,
    hasBottomSeparator = false,
    onEmpty,
    hideHeader = false,
    trailingItem,
    itemsToShow: itemsToShowOverride,
    stepSize,
    isInfinite,
    showArrows,
    arrowVariant,
    gapSize,
    scrollPadding,
    shouldAdjustFlexBasis,
    itemFlexBasis,
    edgeFade,
    currency,
    tileSize = TILE_SIZES.small,
    ...props
}) => {
    const data = usePreloadedQuery<RecentlyViewedItemsContextQuery>(
        recentlyViewedItemsQuery,
        queryReference
    );
    const user = useFragment<RecentlyViewedItemsCarousel_user$key>(
        userFragment,
        data.viewer?.user || null
    );
    const [viewer, refetch] = useRefetchableFragment<
        RecentlyViewedItemsCarouselViewerRefetchQuery,
        RecentlyViewedItemsCarousel_viewer$key
    >(viewerFragment, data.viewer);
    const itemsRef = useFragment<RecentlyViewedItemsCarousel_items$key>(
        itemsFragment,
        data?.viewer?.itemRead ? data.viewer.itemRead.filter(filterNulls) : null
    );

    const { items, hasFetchedItems } = useRecentHistoryList({ user, itemsRef });
    const { userId, deviceType, itemsPerPage, showPrice } = useRecentlyViewedItemsContext();
    const isMobile = deviceType === MOBILE;
    const itemsIds = useMemo(() => items?.map(item => item.itemId), [items]);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const onVisibilityChange: (props: VisibilityChangeCallback) => void = useCallback(
        ({ isVisible, disconnect }) => {
            if (isVisible && onInitialDisplay && items?.length) {
                onInitialDisplay({ items });
                disconnect();
            }
        },
        [onInitialDisplay, items]
    );

    useVisibilityTracker({
        elementRef: wrapperRef,
        onVisibilityChange: onVisibilityChange,
        isEnabled: !!onInitialDisplay,
    });

    useEffect(() => {
        /**
         * Refetch favorites data if recent history contains fetched items
         */
        if (hasFetchedItems && itemsIds && userId) {
            // refetch suspends, and this runs after the carousel is already on screen. Without a
            // transition the boundary falls back to the loading shimmer and the items disappear.
            startTransition(() => {
                refetch({
                    loadPortfolioData: true,
                    selectedItemIds: itemsIds,
                    userIds: userId ? [userId] : [],
                });
            });
        }
    }, [refetch, itemsIds, userId, hasFetchedItems]);

    useEffect(() => {
        if (!items?.length) {
            onEmpty?.();
        }
    }, [items, onEmpty]);

    if (!items?.length) {
        // Hold space until onEmpty unmounts this feed slot; null paints a gap.
        return onEmpty ? (
            <div
                className={classnames(dibsCss.flex, dibsCss.justifyCenter, dibsCss.itemsCenter)}
                style={{ height: getTileImageHeightPx(tileSize) }}
            >
                <Spinner />
            </div>
        ) : null;
    }

    const onArrowClick = ({ direction }: { direction: 'previous' | 'next' }): void => {
        trackArrowClick({ direction, actionLabel });
    };

    const itemsToShow = itemsToShowOverride ?? itemsPerPage;

    return (
        <RecentlyViewedItemsWrapper
            wrapperClassName={wrapperClassName}
            actionLabel={actionLabel}
            trackViewMoreClick={trackViewMoreClick}
            hasTopSeparator={hasTopSeparator}
            hasBottomSeparator={hasBottomSeparator}
            hideHeader={hideHeader}
            ref={wrapperRef}
        >
            <ScrollCarousel
                animation="slide"
                enableScroll
                itemsToShow={itemsToShow}
                dataTn="recently-viewed-items-carousel"
                noGap
                gapSize={gapSize ?? 'small'}
                stepSize={stepSize ?? (isMobile ? 1 : Math.floor(itemsToShow))}
                isInfinite={isInfinite ?? !isMobile}
                showArrows={showArrows ?? !isMobile}
                arrowVariant={arrowVariant}
                scrollPadding={scrollPadding ?? (isMobile ? 'small' : 'none')}
                shouldAdjustFlexBasis={shouldAdjustFlexBasis}
                itemFlexBasis={itemFlexBasis}
                edgeFade={edgeFade}
                onArrowClick={onArrowClick}
            >
                {items.map((item, index) => {
                    // item carries both image-only and image-with-price fragment refs
                    const tileProps = {
                        item,
                        dataTn: 'recently-viewed-tile',
                        imageBackgroundColor: 'moonStone' as const,
                        index,
                        itemsPerRow: 0, // lazy load all images in the carousel
                        onClick: () => {
                            (props.trackItemClick || trackItemClick)({
                                item,
                                index,
                                actionLabel,
                            });
                        },
                        onContentLoaded: () => {
                            (props.trackItemImpression || trackItemImpression)({
                                items: [item],
                                index,
                                actionLabel,
                            });
                        },
                        size: tileSize,
                        srcSet: { sizes: getSrcsetString(item.itemImageUrl) },
                        favoritesComponent: (
                            <div
                                className={classnames(
                                    dibsCss.absolute,
                                    dibsCss.top0,
                                    dibsCss.right0,
                                    dibsCss.mtXxsmall,
                                    dibsCss.prXxsmall
                                )}
                            >
                                <RecentlyViewedItemsHeart
                                    isMobile={isMobile}
                                    currentItem={item}
                                    viewer={viewer}
                                    user={user}
                                    items={itemsRef}
                                    authModalLoader={authModalLoader}
                                />
                            </div>
                        ),
                    };

                    return showPrice && currency ? (
                        <BuyerProductImageWithPrice
                            key={index}
                            currency={currency}
                            {...tileProps}
                        />
                    ) : (
                        <BuyerProductImageOnly key={index} {...tileProps} />
                    );
                })}
                {trailingItem}
            </ScrollCarousel>
        </RecentlyViewedItemsWrapper>
    );
};
